import {AddItemResponseStatus_Success_Section_BasketUrgency_ComposedHint} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {COLORS, Image, Text, tabletAndUpLinaria} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect, useRef, useState} from 'react';
import {useRect} from 'react-viewport-utils';
import {BasketUrgencyHintWrapper} from './BasketUrgencyHintWrapper';

const MIN_BASKET_WRAPPER_HEIGHT = 34;

export const BasketUrgencyComposedHint: React.FC<{
  composedHint: AddItemResponseStatus_Success_Section_BasketUrgency_ComposedHint;
}> = ({composedHint}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const wrapperRect = useRect(ref, {disableScrollUpdates: true});
  const [isWrapping, setIsWrapping] = useState(false);

  useEffect(() => {
    // The basket urgency hint layout differs based on whether the hints can fit within the screen size.
    // If the hints fit the screen size the height of the BasketUrgency wrapper will be 34px, otherwise this will be taller.
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setIsWrapping(
      Boolean(wrapperRect && wrapperRect.height > MIN_BASKET_WRAPPER_HEIGHT),
    );
  }, [wrapperRect]);

  return (
    <>
      <Headline
        size="px12"
        textAlignment="center"
        as="p"
        $foregroundColor={composedHint.foregroundColor.hex}
      >
        {composedHint.headline}
      </Headline>
      <StyledBasketUrgencyHintWrapper
        ref={ref}
        data-is-wrapping={isWrapping}
        backgroundColor={composedHint.backgroundColor}
        infoBox={composedHint.infoBox}
      >
        {composedHint.elements.map((element, index) => (
          <Hint
            key={index}
            data-show-divider={index !== composedHint.elements.length - 1}
          >
            <StyledImage
              data-testid="basketUrgencyHintIcon"
              alt={element.icon.description}
              breakpoints={[{columns: 1}]}
              ratio="1:1"
              src={element.icon.url}
              loading="lazy"
              widths={[18, 36, 54]}
            />
            <Text size="px12" data-testid="basketUrgencyHintText">
              {element.text}
            </Text>
          </Hint>
        ))}
      </StyledBasketUrgencyHintWrapper>
    </>
  );
};

const StyledBasketUrgencyHintWrapper = styled(BasketUrgencyHintWrapper)`
  display: flex;
  flex-wrap: wrap;
  min-height: ${MIN_BASKET_WRAPPER_HEIGHT}px;

  &[data-is-wrapping='true'] {
    gap: 8px;

    ${tabletAndUpLinaria(`
      padding-bottom: 18px;
    `)}
  }
`;

const Headline = styled(Text)<{
  $foregroundColor: string;
}>`
  color: ${props => props.$foregroundColor};
  text-align: center;
  margin-bottom: 12px;

  ${tabletAndUpLinaria('margin-block: 12px;')}
`;

const Hint = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  flex: 1 0 50%;
  gap: 5px;

  &[data-show-divider='true'] {
    &:after {
      content: '';
      position: absolute;
      background-color: ${COLORS.Black};

      // horizontal divider
      [data-is-wrapping='true'] & {
        width: 40px;
        height: 1px;
        bottom: -6px;
      }
      // vertical divider
      [data-is-wrapping='false'] & {
        width: 1px;
        height: 18px;
        right: 0;
      }
    }
  }
`;

const StyledImage = styled(Image)`
  width: 18px;
  height: 18px;
  margin-right: 5px;
`;
